import { useEffect, useCallback, Suspense } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { usePMAuth } from '@/contexts/PMAuthContext';
import { CreditCard } from 'lucide-react';
import { PMSidebar } from '@/components/pm/PMSidebar';
import { PMHeader } from '@/components/pm/PMHeader';
import { PMRouteGuard } from '@/components/pm/PMRouteGuard';
import { usePMSessionTimeout } from '@/hooks/usePMSessionTimeout';
import SessionTimeoutCountdown from '@/components/SessionTimeoutCountdown';
import { PortalAuthGuard, PortalLoadingSkeleton } from '@/components/portal/PortalAuthGuard';

export default function PMLayout() {
  const { user, pmUser, loading, pmUserLoaded, signOut, activeProgramManagerId } = usePMAuth();
  const { showCountdown, countdownSeconds, resetTimer } = usePMSessionTimeout();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/pm/login', { replace: true });
    }
  }, [loading, user, navigate]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/pm/login', { replace: true });
  }, [signOut, navigate]);

  // Effective profile = pmUser AND activeProgramManagerId (need both)
  const profileForGuard = pmUser && activeProgramManagerId ? pmUser : null;

  return (
    <PortalAuthGuard
      loading={loading}
      profileLoaded={pmUserLoaded}
      user={user}
      profile={profileForGuard}
      onSignOut={handleSignOut}
      brandIcon={<CreditCard className="w-10 h-10 text-accent" />}
      brandLabel="portal"
      accessDeniedMessage="Your account does not have Program Manager access. Contact your administrator for access."
      retryWaitedMessage="Unable to load your program manager profile. Please try again or contact your administrator."
    >
      {showCountdown && (
        <SessionTimeoutCountdown
          secondsLeft={countdownSeconds}
          onStayLoggedIn={resetTimer}
          portalName="PM Portal"
        />
      )}
      <div className="h-screen bg-background flex flex-col overflow-hidden">
        <PMHeader />
        <div className="flex flex-1 min-h-0 overflow-hidden">
          <PMSidebar />
          <main className="flex-1 overflow-auto">
            {profileForGuard ? (
              <PMRouteGuard>
                <Suspense fallback={<PortalLoadingSkeleton />}>
                  <div className="p-6">
                    <Outlet />
                  </div>
                </Suspense>
              </PMRouteGuard>
            ) : (
              <PortalLoadingSkeleton />
            )}
          </main>
        </div>
      </div>
    </PortalAuthGuard>
  );
}
